import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import { api } from "../lib/api";

const SECTIONS = [
  {
    bucket: "voice",
    title: "Márkahang",
    eyebrow: "MÁRKAHANG",
    typeLabel: "Márkahang",
    addLabel: "+ Új Márkahang",
    href: "/agents/voice?new=1",
  },
  {
    bucket: "client",
    title: "Avatarok",
    eyebrow: "AVATAR",
    typeLabel: "Avatar",
    addLabel: "+ Új Avatar",
    href: "/agents/client?new=1",
  },
  {
    bucket: "offer",
    title: "Ajánlatok",
    eyebrow: "AJÁNLAT",
    typeLabel: "Ajánlat",
    addLabel: "+ Új Ajánlat",
    href: "/agents/offer?new=1",
  },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

/* ---------- Per-agent blueprint summary extractors ---------- */

function summarizeVoice(item) {
  const bp = item.blueprint || {};
  const arch = bp.archetipus || {};
  const fo = (arch.fo && arch.fo.nev) || "";
  const masodlagos = (arch.masodlagos && arch.masodlagos.nev) || "";
  const archetypeLine = [fo, masodlagos].filter(Boolean).join(" · ");

  // Top values: derive from "ki_vagyok" assertions or fall back to hangnem.
  const ki = bp.ki_vagyok_ki_nem_vagyok || {};
  const kiVagyok = Array.isArray(ki.ki_vagyok) ? ki.ki_vagyok : [];
  const hangnem = Array.isArray(bp.hangnem) ? bp.hangnem : [];
  const valuesPool = kiVagyok.length > 0 ? kiVagyok : hangnem;
  const top3 = valuesPool.slice(0, 3).map((v) => String(v).split(/[.,]/)[0].trim()).filter(Boolean);

  // Card title — use saved name (typically "Brand Voice - {name}" or similar).
  const title = item.name || "Saját márkahang";

  return {
    title,
    line1: archetypeLine ? `Archetípus: ${archetypeLine}` : null,
    line2: top3.length > 0 ? `${top3.length} fő érték: ${top3.join(", ")}` : null,
  };
}

function summarizeAvatar(item) {
  const bp = item.blueprint || {};
  const ak = bp.alapkep || bp.alapadatok || {};
  // ELSŐDLEGES: a user által a Mentés modálban megadott név (item.name).
  // FALLBACK: a blueprint reprezentatív neve (ak.nev). Ha egyik sincs,
  // generikus „Avatar". A „(reprezentatív név)" felirat csak akkor jelenik
  // meg, ha tényleg a blueprint-belső nevet mutatjuk, nem a usernevét.
  const userName = (item.name || "").trim();
  const bpNev = (ak.nev || "").trim();
  const title = userName || bpNev || "Avatar";
  const showRepLabel = !userName && !!bpNev;
  const kor = (ak.kor || "").toString().trim();
  const szakma = (ak.szakma || "").trim();

  const lineA = [
    kor ? (/^\d/.test(kor) ? `${kor} éves` : kor) : null,
    szakma || null,
  ].filter(Boolean).join(" ");

  // Primary desire (single short phrase).
  const vagyai = bp.vagyai || {};
  const vagy = (vagyai.kulso_cel || vagyai.belso_vagy || "").toString().split(/[.;]/)[0].trim();

  return {
    title,
    titleSuffix: showRepLabel ? "(reprezentatív név)" : "",
    line1: lineA || null,
    line2: vagy ? `Vágy: ${vagy}` : null,
  };
}

function summarizeOffer(item) {
  const bp = item.blueprint || {};
  const snap = bp.snapshot || {};
  const program = (snap.program_nev || bp.ajanlat_nev || bp.program_name || "").trim() || (item.name || "Ajánlat");

  const avatarLine = (snap.celkozonseg_avatar_neve || snap.avatar_nev || snap.avatar || "").toString().trim();

  const ar = bp.ar_es_kockazatcsokkentes || {};
  const fo_ar = (ar.fo_ar || ar.ar || "").toString().trim();
  const tartam = (snap.idotav || snap.hossz || ar.idotav || "").toString().trim();
  const formatum = (snap.formatum || snap.format || "").toString().trim();

  const meta = [fo_ar, tartam, formatum].filter(Boolean).join(" · ");

  return {
    title: program,
    line1: avatarLine ? `Avatar: ${avatarLine}` : null,
    line2: meta || null,
  };
}

const SUMMARIZERS = {
  voice: summarizeVoice,
  client: summarizeAvatar,
  offer: summarizeOffer,
};

const ROUTES = {
  voice: "/agents/voice",
  client: "/agents/client",
  offer: "/agents/offer",
};

/* ---------- Card ---------- */

function EntryCard({ item, eyebrow, onClick }) {
  const summarize = SUMMARIZERS[item.agent_type] || (() => ({ title: item.name }));
  const s = summarize(item);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`identity-card-${item.id}`}
      style={{
        background: "rgba(94, 80, 247, 0.06)",
        border: "1px solid rgba(94, 80, 247, 0.30)",
        borderRadius: 12,
        padding: 18,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        color: "inherit",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        transition: "background 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(94, 80, 247, 0.10)";
        e.currentTarget.style.borderColor = "rgba(94, 80, 247, 0.45)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(94, 80, 247, 0.06)";
        e.currentTarget.style.borderColor = "rgba(94, 80, 247, 0.30)";
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "#7c6cf2",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        {eyebrow}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: "#e8e8f0",
          marginBottom: 4,
          letterSpacing: "-0.005em",
        }}
      >
        {s.title}
        {s.titleSuffix && (
          <span
            style={{
              fontSize: 11,
              color: "#6a6a80",
              fontStyle: "italic",
              fontWeight: 400,
              marginLeft: 4,
            }}
          >
            {s.titleSuffix}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#9898ad", lineHeight: 1.5 }}>
        {s.line1 && <div>{s.line1}</div>}
        {s.line2 && <div>{s.line2}</div>}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#6a6a80",
          marginTop: 10,
        }}
      >
        Mentve: {formatDate(item.saved_at)}
      </div>
    </button>
  );
}

/* ---------- Empty state ---------- */

function EmptyState({ typeLabel }) {
  return (
    <div
      data-testid={`identity-empty-${typeLabel}`}
      style={{
        gridColumn: "1 / -1",
        background: "rgba(255,255,255,0.02)",
        border: "1px dashed rgba(255,255,255,0.10)",
        borderRadius: 12,
        padding: "28px 20px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 14, color: "#9898ad" }}>
        Még nincs mentett {typeLabel}.
      </div>
      <div style={{ fontSize: 12, color: "#6a6a80", marginTop: 6 }}>
        Kattints a »+ Új {typeLabel}« gombra a létrehozáshoz.
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

export default function Identity() {
  const navigate = useNavigate();
  const [data, setData] = useState({ voice: [], client: [], offer: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/identity");
        if (!alive) return;
        setData({
          voice: Array.isArray(res.data?.voice) ? res.data.voice : [],
          client: Array.isArray(res.data?.client) ? res.data.client : [],
          offer: Array.isArray(res.data?.offer) ? res.data.offer : [],
        });
      } catch {
        if (alive) setData({ voice: [], client: [], offer: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleCardClick = (item) => {
    const base = ROUTES[item.agent_type] || "/dashboard";
    navigate(`${base}?saved_id=${encodeURIComponent(item.id)}`);
  };

  return (
    <div className="p-8 md:p-12 max-w-[1200px] mx-auto" data-testid="identity-page">
      {/* Header */}
      <div style={{ marginBottom: 36 }} data-testid="identity-header">
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#7c6cf2",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          IDENTITÁS
        </div>
        <h1
          className="font-heading"
          style={{
            fontSize: 32,
            fontWeight: 600,
            color: "#e8e8f0",
            marginBottom: 8,
            letterSpacing: "-0.018em",
            lineHeight: 1.1,
          }}
        >
          Identitás
        </h1>
        <p
          style={{
            fontSize: 14,
            color: "#9898ad",
            maxWidth: 600,
            lineHeight: 1.5,
          }}
        >
          Márkahangok, avatarok és ajánlatok, amelyekből a rendszer dolgozik.
        </p>
      </div>

      {SECTIONS.map((section, idx) => {
        const items = data[section.bucket] || [];
        const isLast = idx === SECTIONS.length - 1;
        return (
          <section
            key={section.bucket}
            data-testid={`identity-section-${section.bucket}`}
            style={{ marginBottom: isLast ? 0 : 40 }}
          >
            {/* Section header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
                paddingBottom: 14,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#e8e8f0",
                  letterSpacing: "-0.01em",
                }}
              >
                {section.title}
              </div>
              <button
                type="button"
                onClick={() => navigate(section.href)}
                data-testid={`identity-add-${section.bucket}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "rgba(94, 80, 247, 0.10)",
                  border: "1px solid rgba(94, 80, 247, 0.40)",
                  borderRadius: 8,
                  padding: "7px 14px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#8a7cff",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(94, 80, 247, 0.18)";
                  e.currentTarget.style.borderColor = "rgba(94, 80, 247, 0.55)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(94, 80, 247, 0.10)";
                  e.currentTarget.style.borderColor = "rgba(94, 80, 247, 0.40)";
                }}
              >
                <Plus size={11} weight="bold" />
                {section.addLabel.replace(/^\+\s*/, "")}
              </button>
            </div>

            {/* Card grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 14,
              }}
              data-testid={`identity-grid-${section.bucket}`}
            >
              {loading ? null : items.length === 0 ? (
                <EmptyState typeLabel={section.typeLabel} />
              ) : (
                items.map((item) => (
                  <EntryCard
                    key={item.id}
                    item={item}
                    eyebrow={section.eyebrow}
                    onClick={() => handleCardClick(item)}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
